import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users,
  Eye,
  Heart,
  Share2,
  MousePointer,
  Calendar,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Youtube
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AnalyticsPage = ({ user, logout, token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [period, setPeriod] = useState("30");
  const [loading, setLoading] = useState(true);

  const platformColors = {
    instagram: "#E1306C",
    twitter: "#1DA1F2",
    facebook: "#4267B2",
    linkedin: "#0077B5",
    youtube: "#FF0000",
    tiktok: "#000000",
    pinterest: "#E60023"
  };

  const platformIcons = {
    twitter: <Twitter className="w-5 h-5" />,
    instagram: <Instagram className="w-5 h-5" />,
    facebook: <Facebook className="w-5 h-5" />,
    linkedin: <Linkedin className="w-5 h-5" />,
    youtube: <Youtube className="w-5 h-5" />,
    tiktok: <span className="text-sm font-bold">TT</span>,
    pinterest: <span className="text-sm font-bold">P</span>
  };

  useEffect(() => {
    fetchData();
  }, [token, period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [analyticsRes, performanceRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/overview`, { headers }),
        axios.get(`${BACKEND_URL}/api/analytics/performance?days=${period}`, { headers })
      ]);
      
      setAnalytics(analyticsRes.data);
      setPerformance(performanceRes.data.performance);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const platformData = analytics?.platform_stats 
    ? Object.entries(analytics.platform_stats).map(([name, stats]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        followers: stats.followers,
        engagement: stats.engagement,
        growth: stats.growth,
        color: platformColors[name]
      }))
    : [];

  const pieData = platformData.map(p => ({
    name: p.name,
    value: p.followers,
    color: p.color
  }));

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Analytics</h1>
              <p className="text-muted-foreground mt-1">
                Analiza el rendimiento de tu contenido
              </p>
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="period-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Overview Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card data-testid="stat-reach">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Alcance Total</p>
                        <p className="text-3xl font-bold mt-1">
                          {analytics?.engagement?.total_reach?.toLocaleString()}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Eye className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-sm text-green-600">
                      <TrendingUp className="w-4 h-4" />
                      <span>+{analytics?.engagement?.growth}% vs. periodo anterior</span>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-interactions">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Interacciones</p>
                        <p className="text-3xl font-bold mt-1">
                          {analytics?.engagement?.total_interactions?.toLocaleString()}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Heart className="w-6 h-6 text-accent" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-engagement-rate">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Tasa de Engagement</p>
                        <p className="text-3xl font-bold mt-1">
                          {analytics?.engagement?.avg_engagement_rate}%
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-chart-3/20 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-chart-3" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-posts">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Posts Publicados</p>
                        <p className="text-3xl font-bold mt-1">
                          {analytics?.posts?.published}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-chart-4/30 rounded-lg flex items-center justify-center">
                        <Share2 className="w-6 h-6 text-chart-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid lg:grid-cols-12 gap-6 mb-8">
                {/* Performance Chart */}
                <Card className="lg:col-span-8" data-testid="performance-chart">
                  <CardHeader>
                    <CardTitle className="text-lg">Rendimiento en el Tiempo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={performance}>
                          <defs>
                            <linearGradient id="colorReachFull" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22D3EE" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#22D3EE" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorEngagementFull" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#A855F7" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#A855F7" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => {
                              const [y, m, d] = value.split('-');
                              return `${d}/${m}`;
                            }}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                          />
                          <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              color: 'hsl(var(--foreground))'
                            }}
                            labelFormatter={(value) => `Fecha: ${value}`}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="reach" 
                            stroke="#22D3EE" 
                            strokeWidth={2}
                            fillOpacity={1} 
                            fill="url(#colorReachFull)" 
                            name="Alcance"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="engagement" 
                            stroke="#A855F7" 
                            strokeWidth={2}
                            fillOpacity={1} 
                            fill="url(#colorEngagementFull)" 
                            name="Engagement"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Audience Distribution */}
                <Card className="lg:col-span-4" data-testid="audience-chart">
                  <CardHeader>
                    <CardTitle className="text-lg">Distribución de Audiencia</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => value.toLocaleString()}
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              color: 'hsl(var(--foreground))'
                            }}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Platform Breakdown */}
              <Card data-testid="platform-breakdown">
                <CardHeader>
                  <CardTitle className="text-lg">Desglose por Plataforma</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analytics?.platform_stats && Object.entries(analytics.platform_stats).map(([platform, stats]) => (
                      <div 
                        key={platform}
                        className="p-6 border border-border rounded-lg"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <span 
                            className="p-2 rounded-lg text-white"
                            style={{ backgroundColor: platformColors[platform] }}
                          >
                            {platformIcons[platform]}
                          </span>
                          <div>
                            <p className="font-semibold capitalize">{platform}</p>
                            <p className="text-sm text-muted-foreground">
                              {stats.followers.toLocaleString()} seguidores
                            </p>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Engagement</span>
                            <span className="font-medium">{stats.engagement}%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Crecimiento</span>
                            <span className={`font-medium flex items-center gap-1 ${stats.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {stats.growth > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                              {stats.growth > 0 ? '+' : ''}{stats.growth}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Best Times */}
              <Card className="mt-6" data-testid="best-times-detailed">
                <CardHeader>
                  <CardTitle className="text-lg">Mejores Horarios para Publicar</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics?.best_times || []}>
                        <XAxis 
                          dataKey="day" 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        />
                        <YAxis 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          domain={[0, 100]}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            color: 'hsl(var(--foreground))'
                          }}
                          formatter={(value, name, props) => [
                            `${value} - ${props.payload.time}`,
                            'Puntuación'
                          ]}
                        />
                        <Bar 
                          dataKey="score" 
                          fill="#22D3EE"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm text-muted-foreground text-center mt-4">
                    Los horarios se basan en la actividad de tu audiencia
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
};
